/**
 * Timestamp for a touchpoint inserted at a chosen position in the journey —
 * PRD docs/prd/attribution-korrektur.md Story S4, Epic A4.
 *
 *  - between two neighbors: the midpoint.
 *  - "auf derselben Sekunde" (identical neighbors, or a gap below the
 *    resolution): the smallest possible step after the PREVIOUS neighbor
 *    instead of a midpoint — a midpoint would round back to one of the two
 *    timestamps and lose the intended order. Clamped below the NEXT
 *    neighbor too, so a sub-millisecond-resolution gap can't push the step
 *    onto (or past) it (F2, TASK-59 review).
 *  - before the first event / after the last: shortly before / after it.
 *  - empty journey (no neighbors at all): null — the operator enters the
 *    timestamp by hand.
 *
 * Pure (no Date.now()) — deterministic to test. Neighbors are ISO-8601
 * strings, matching CustomerJourneyEvent.createdAt.
 */

/** "Dieselbe Sekunde" — the resolution named in the Issue/Epic/PRD text. */
const RESOLUTION_MS = 1000;
/** Smallest step: created_at is ISO-8601 with millisecond precision. */
const STEP_MS = 1;

export function computeInsertTimestamp(
  prevAt: string | null,
  nextAt: string | null,
): string | null {
  if (prevAt === null && nextAt === null) return null;
  if (prevAt === null) {
    return new Date(new Date(nextAt!).getTime() - RESOLUTION_MS).toISOString();
  }
  if (nextAt === null) {
    return new Date(new Date(prevAt).getTime() + RESOLUTION_MS).toISOString();
  }
  const prevMs = new Date(prevAt).getTime();
  const nextMs = new Date(nextAt).getTime();
  if (nextMs - prevMs < RESOLUTION_MS) {
    const step = prevMs + STEP_MS;
    // With a gap under STEP_MS (e.g. neighbors 1ms apart), the naive step
    // reaches or passes nextAt — clamp it below instead.
    return new Date(
      nextMs > prevMs ? Math.min(step, nextMs - STEP_MS) : step,
    ).toISOString();
  }
  return new Date(prevMs + Math.round((nextMs - prevMs) / 2)).toISOString();
}

/**
 * Which timestamp is actually submitted when inserting a touchpoint. While
 * the operator hasn't edited the time field by hand, the precisely computed
 * value is authoritative — the <input type="datetime-local"> only round-trips
 * to minute precision, and submitting THAT would silently break the
 * ordering `computeInsertTimestamp` worked out (F1, TASK-59 review).
 */
export function resolveInsertDate(
  computed: string | null,
  fieldEdited: boolean,
  fieldValue: string,
): Date {
  return !fieldEdited && computed !== null
    ? new Date(computed)
    : new Date(fieldValue);
}

/** Local-time value for <input type="datetime-local"> — keeps seconds (the
 *  input carries step="1") so what is shown matches what gets inserted; the
 *  computed timestamp routinely lands off a whole minute. */
export function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 19);
}

/**
 * (computed, datetime) state to hold after the journey's neighbors change
 * while the insert form is still open — e.g. "Jetzt laden" pulls an older
 * event in and shifts `nextAt` at the frontmost mark (TASK-61 review, F1).
 * Without this, the form kept the stale `computed`/`datetime` from when it
 * opened, so submitting unedited silently wrote the touchpoint behind the
 * newly loaded events instead of in front of them.
 *
 * `computed` always tracks the current neighbors — it drives the hint text
 * and, while the field is untouched, the submitted value via
 * `resolveInsertDate`. `datetime` is `null` whenever the operator already
 * edited the field by hand: that's the caller's signal to leave the
 * displayed value alone rather than overwrite a manual edit.
 */
export function recomputeInsertState(
  prevAt: string | null,
  nextAt: string | null,
  fieldEdited: boolean,
): { computed: string | null; datetime: string | null } {
  const computed = computeInsertTimestamp(prevAt, nextAt);
  return {
    computed,
    datetime: fieldEdited
      ? null
      : computed
        ? toLocalInputValue(computed)
        : "",
  };
}
